<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { Product } from '@shared/types/domain';
import ModalDialog from '@shared/components/ModalDialog.vue';
import LoadingButton from '@shared/components/LoadingButton.vue';
import { useCartStore } from '../../stores/cart-store';
import { getStaticHostUrl } from '../../core/static-host';

// Sub-components and Composable
import { useProductDetail } from '../../composables/menu/use-product-detail';
import { useAppConfig } from '../../composables/use-app-config';
import ModifierGroup from './detail/ModifierGroup.vue';
import PackageComponents from './detail/PackageComponents.vue';

const props = defineProps<{
  modelValue: boolean;
  product: Product | null;
  editIndex?: number | null;
  initialItem?: any;
}>();
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void; (e: 'added'): void; (e: 'updated'): void }>();
const cartStore = useCartStore();

// Pass a getter so validation tracks product when MenuPage opens the modal
// after mount (selectedProduct starts null → later assigned).
const {
  quantity, selectedModifiers, selectedComponentModifiers, NO_SELECTION_PREFIX,
  getComponentGroupKey, resetState, calculateExtrasTotal, groupAllowsNoSelection,
  getNoSelectionId, getSelectionCount, getGroupMaxSelection, getGroupMinSelection,
  getComponentSelectionCount, getComponentGroupMaxSelection, getComponentGroupMinSelection,
  invalidGroups, isSelectionValid, toggleModifier, toggleComponentModifier
} = useProductDetail(() => props.product);

watch(
  () => [props.product, props.initialItem],
  () => resetState(props.product, props.initialItem),
  { immediate: true, deep: true }
);

const totalPrice = computed(() => {
  if (!props.product) return 0;
  return (props.product.price + calculateExtrasTotal()) * quantity.value;
});

const appConfig = useAppConfig();
const formattedTotal = computed(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: appConfig.config.currency_code || 'USD' }).format(totalPrice.value || 0));

const categoryPath = computed(() => {
  if (!props.product) return '';
  const c = String(props.product.menu_category_name || '').trim();
  const s = String(props.product.menu_subcategory_name || '').trim();
  return c && s ? `${c} / ${s}` : (s || c || 'Sin subcategoría');
});

const serviceModeLabel = computed(() => props.product?.is_quick_serve ? 'Entrega inmediata.' : 'Preparación en cocina.');
const serviceModeClass = computed(() => props.product?.is_quick_serve ? 'mode-quick' : 'mode-prep');

const offerLabel = computed(() => {
  const labels = Array.isArray(props.product?.labels) ? props.product?.labels : [];
  const promo = labels.find(l => ['promo', 'discount', 'offer'].includes(String(l?.label_type || '').toLowerCase()));
  if (promo?.name) return String(promo.name);
  if (props.product?.has_active_promotion) return 'Oferta';
  return '';
});

const formatSelectionCounter = (c: number, g: any) => {
  const min = getGroupMinSelection(g); const max = getGroupMaxSelection(g);
  return min > 0 ? `${c}/${max} (mín: ${min})` : `${c}/${max}`;
};

const formatComponentSelectionCounter = (c: number, g: any) => {
  const min = getComponentGroupMinSelection(g); const max = getComponentGroupMaxSelection(g);
  return min > 0 ? `${c}/${max} (mín: ${min})` : `${c}/${max}`;
};

const buttonLabel = computed(() => {
  if (!isSelectionValid.value) {
    return 'Selecciona opciones obligatorias (*)';
  }
  if (props.editIndex != null && props.editIndex >= 0) {
    return `Guardar cambios · ${formattedTotal.value}`;
  }
  return `Agregar ${quantity.value > 1 ? quantity.value : ''} al carrito · ${formattedTotal.value}`;
});

const handleAddToCart = () => {
  if (!props.product || !isSelectionValid.value) return;
  const item = {
    id: props.product.id,
    order_item_id: props.initialItem?.order_item_id,
    menu_item_id: props.product.id,
    name: props.product.name,
    price: props.product.price,
    quantity: quantity.value,
    image: imageSource.value,
    modifiers: Object.values(selectedModifiers.value).flat().filter(id => !String(id).startsWith(NO_SELECTION_PREFIX)),
    modifier_none_groups: Object.entries(selectedModifiers.value).filter(([, v]) => v.some(id => String(id).startsWith(NO_SELECTION_PREFIX))).map(([id]) => id),
    modifier_groups: (props.product.modifier_groups || []).map(g => ({ ...g, modifiers: g.modifiers || [] })),
    package_components: (props.product.package_components || []).map((comp, idx) => {
      const selected: any[] = []; const none: any[] = [];
      (comp.modifier_groups || []).forEach(g => {
        const key = getComponentGroupKey(comp, idx, g.id);
        (selectedComponentModifiers.value[key] || []).forEach(v => String(v).startsWith(NO_SELECTION_PREFIX) ? none.push(String(g.id)) : selected.push(v));
      });
      return { component_item_id: comp.component_item_id, menu_item_id: comp.menu_item_id || comp.component_item_id, name: comp.name, quantity: comp.quantity, modifiers: selected, modifier_none_groups: none };
    }),
    extrasTotal: calculateExtrasTotal(),
    special_instructions: ''
  };

  if (props.editIndex != null && props.editIndex >= 0) {
    cartStore.updateItem(props.editIndex, item as unknown as Parameters<typeof cartStore.addItem>[0]);
    emit('updated');
  } else {
    cartStore.addItem(item as unknown as Parameters<typeof cartStore.addItem>[0]);
    emit('added');
  }
  emit('update:modelValue', false);
};

const normalizeUrl = (url: string | null | undefined): string => {
  const r = String(url || '').trim(); if (!r) return '';
  const h = getStaticHostUrl();
  const p = r.replace(/\/assets\/cafeteria?-a-de-prueba\//g, '/assets/enfa/');
  return (r.startsWith('http') || !h) ? p : `${h}${p}`;
};

const DEFAULT_SVG_PLACEHOLDER = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200" fill="%23f1f5f9"><rect width="300" height="200" fill="%23e2e8f0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%2394a3b8">Sin imagen</text></svg>`;

const imageSource = computed(() => normalizeUrl((props.product as any)?.image_path || (props.product as any)?.image_url) || DEFAULT_SVG_PLACEHOLDER);

const currentImg = ref(imageSource.value);

watch(
  () => imageSource.value,
  (next) => {
    currentImg.value = next;
  },
  { immediate: true },
);

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement | null;
  if (!target) return;
  if (currentImg.value !== DEFAULT_SVG_PLACEHOLDER) currentImg.value = DEFAULT_SVG_PLACEHOLDER;
};

const hasModImg = (m: any) => Boolean(String(m?.image_path || m?.image_url || '').trim());
const getModImg = (m: any) => normalizeUrl(m?.image_path || m?.image_url) || DEFAULT_SVG_PLACEHOLDER;
</script>

<template>
  <ModalDialog :model-value="modelValue" :title="product?.name || 'Detalles'" size="large" @update:model-value="emit('update:modelValue', $event)">
    <div v-if="product" class="product-detail">
      <div class="product-detail__image">
        <img :src="currentImg" :alt="product.name" @error="handleImageError" />
        <div v-if="offerLabel" class="product-detail__offer-badge">{{ offerLabel }}</div>
      </div>

      <div class="product-detail__info">
        <p class="classification">{{ categoryPath }}</p>
        <p class="description">{{ product.description }}</p>
        <div class="service-mode-chip" :class="serviceModeClass">{{ serviceModeLabel }}</div>
      </div>

      <div v-if="invalidGroups.length" class="validation-warning-banner">
        ⚠️ Selecciona los campos obligatorios (*): <strong>{{ invalidGroups.join(', ') }}</strong>
      </div>

      <div v-if="product.modifier_groups?.length" class="product-detail__modifiers">
        <ModifierGroup
          v-for="group in product.modifier_groups" :key="group.id"
          :group="group" :selected-ids="selectedModifiers[String(group.id)] || []"
          :get-selection-count="getSelectionCount" :get-group-max-selection="getGroupMaxSelection"
          :get-group-min-selection="getGroupMinSelection" :format-selection-counter="formatSelectionCounter"
          :group-allows-no-selection="groupAllowsNoSelection" :get-no-selection-id="getNoSelectionId"
          :has-modifier-image="hasModImg" :get-modifier-image-source="getModImg"
          @toggle="toggleModifier" @image-error="handleImageError"
        />
      </div>

      <PackageComponents
        v-if="product.package_components?.length"
        :components="product.package_components" :selected-component-modifiers="selectedComponentModifiers"
        :get-component-group-key="getComponentGroupKey" :get-component-selection-count="getComponentSelectionCount"
        :get-component-group-max-selection="getComponentGroupMaxSelection" :get-component-group-min-selection="getComponentGroupMinSelection"
        :format-component-selection-counter="formatComponentSelectionCounter" :get-no-selection-id="getNoSelectionId"
        :group-allows-no-selection="groupAllowsNoSelection" :has-modifier-image="hasModImg" :get-modifier-image-source="getModImg"
        @toggle="toggleComponentModifier" @image-error="handleImageError"
      />

      <div class="product-detail__quantity">
        <span class="section-title">Cantidad</span>
        <div class="qty-controls">
          <button class="qty-btn" :disabled="quantity <= 1" @click="quantity > 1 && (quantity--)">−</button>
          <span class="qty-value">{{ quantity }}</span>
          <button class="qty-btn" :disabled="quantity >= 20" @click="quantity < 20 && (quantity++)">+</button>
        </div>
      </div>
    </div>

    <!-- ── Fixed footer CTA — outside scrollable body ─── -->
    <template #footer>
      <div class="product-cta-footer">
        <div class="price-summary">
          <span class="label">Total</span>
          <span class="value">{{ formattedTotal }}</span>
        </div>
        <LoadingButton
          variant="primary"
          class="btn-confirm"
          :disabled="!isSelectionValid"
          :label="buttonLabel"
          @click="handleAddToCart"
        />
      </div>
    </template>
  </ModalDialog>
</template>

<style scoped>
.product-detail { display: flex; flex-direction: column; gap: 1rem; }
.product-detail__image { width: 100%; aspect-ratio: 16/9; border-radius: 12px; overflow: hidden; position: relative; }
.product-detail__image img { width: 100%; height: 100%; object-fit: cover; }
.product-detail__offer-badge { position: absolute; top: 0.75rem; left: 0.75rem; border-radius: 999px; padding: 0.35rem 0.75rem; background: #e05a2a; color: #fff; font-size: 0.75rem; font-weight: 800; }
.description { color: var(--muted); font-size: 0.95rem; line-height: 1.5; }
.classification { margin: 0; font-size: 0.82rem; font-weight: 700; color: #334155; }
.validation-warning-banner {
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #c2410c;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.service-mode-chip { display: inline-flex; padding: 0.35rem 0.8rem; border-radius: 999px; font-size: 0.78rem; font-weight: 700; }
.service-mode-chip.mode-quick { background: rgba(22, 163, 74, 0.14); color: #166534; }
.service-mode-chip.mode-prep { background: rgba(37, 99, 235, 0.14); color: #1d4ed8; }
.section-title { font-weight: 700; font-size: 1.1rem; color: var(--text); }
.product-detail__modifiers { display: flex; flex-direction: column; gap: 0.9rem; }
.product-detail__quantity { display: flex; justify-content: space-between; align-items: center; padding-top: 1rem; border-top: 1px solid var(--border); }
.qty-controls { display: flex; align-items: center; gap: 1.5rem; background: #f1f5f9; padding: 0.5rem 1rem; border-radius: 30px; }
.qty-btn { width: 44px; height: 44px; border-radius: 50%; border: none; background: white; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
.qty-value { font-weight: 800; font-size: 1.1rem; min-width: 1.5rem; text-align: center; }
/* Fixed footer CTA — always visible, outside scrollable body */
.product-cta-footer {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.price-summary { display: flex; flex-direction: column; flex-shrink: 0; }
.price-summary .label { font-size: 0.75rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; }
.price-summary .value { font-size: 1.3rem; font-weight: 800; color: var(--color-primary, #e05a2a); }
.btn-confirm { flex: 1; padding: 1rem !important; font-size: 1rem !important; min-height: 52px; min-width: 220px; white-space: nowrap; }

@media (max-width: 768px) {
  .product-detail__image {
    aspect-ratio: 16/9;
    max-height: 12rem;
  }

  .product-cta-footer {
    align-items: center;
  }
}
</style>
